
/**
 * @returns {string} Mevcut domain (örn: mobilassistance.com, bayisatis.com.tr)
 */
export const getCurrentDomain = () => {
    if (typeof window === 'undefined') {
        return '';
    }
    
    const hostname = window.location.hostname;
    
    // Localhost veya development için
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
        return 'development';
    }
    
    return hostname;
};

/**
 * Domain'in .com.tr uzantısına sahip olup olmadığını kontrol eder
 * @param {string} domain - Kontrol edilecek domain
 * @returns {boolean}
 */
export const isComTrDomain = (domain) => {
    if (!domain || domain === 'development') {
        return false;
    }
    
    // .com.tr ile biten domain'leri kontrol et
    if (domain.endsWith('.com.tr') || domain.endsWith('.com.tr:5173')) {
        return true;
    }
    
    // Bayisatis veya mobilassistance domain'lerinde .com.tr kontrolü
    if (domain.includes('.com.tr')) {
        return true;
    }
    
    return false;
};

/**
 * Environment'ı tespit eder (development, uat, production)
 * @returns {string} Environment adı
 */
export const getEnvironment = () => {
    // Vite environment variable'dan al
    const viteEnv = import.meta.env.VITE_ENV;
    
    if (viteEnv) {
        return viteEnv;
    }
    
    // Domain'e göre tespit et
    const domain = getCurrentDomain();
    
    if (domain === 'development' || domain.includes('localhost')) {
        return 'development';
    }
    
    if (domain.includes('uat')) {
        return 'uat';
    }
    
    return 'production';
};

/**
 * Domain ve environment'a göre API base URL'ini oluşturur
 * Domain mapping mantığı:
 * - cagrimerkezi.bayisatis.com veya cagrimerkezi.mobilassistance.com → https://app.mobilassistance.com
 * - cagrimerkezi.bayisatis.com.tr veya cagrimerkezi.mobilassistance.com.tr → https://app.mobilassistance.com.tr
 * 
 * @param {string} customDomain - Özel domain (opsiyonel)
 * @returns {string} API base URL
 */
export const getApiBaseUrl = (customDomain = null) => {
    const domain = customDomain || getCurrentDomain();
    const environment = getEnvironment();
    const isTr = isComTrDomain(domain);
    
    // Development ortamı
    if (domain === 'development' || domain.includes('localhost')) {
        // .env dosyasından gelen değeri kullan
        const envUrl = import.meta.env.VITE_REACT_APP_APIURL;
        if (envUrl) {
            // Development'ta .env'deki URL'i kullan ama domain'e göre .tr ekle
            if (isTr && !envUrl.includes('.tr')) {
                return envUrl.replace('.com', '.com.tr').replace('.com/', '.com.tr/');
            }
            return envUrl;
        }
        // Fallback
        return isTr 
            ? 'http://appuat.mobilassistance.com.tr'
            : 'http://appuat.mobilassistance.com';
    }
    
    // UAT ortamı
    if (environment === 'uat' || domain.includes('uat')) {
        return isTr 
            ? 'http://appuat.mobilassistance.com.tr'
            : 'http://appuat.mobilassistance.com';
    }
    
    // Production ortamı - Domain'e göre API URL belirleme
    // .com.tr domain'leri için .com.tr API URL'i
    // .com domain'leri için .com API URL'i
    return isTr 
        ? 'https://app.mobilassistance.com.tr'
        : 'https://app.mobilassistance.com';
};

/**
 * API URL'ini oluşturur (base URL + path)
 * @param {string} path - API path (örn: /callcenter-service/api/...)
 * @param {string} customDomain - Özel domain (opsiyonel)
 * @returns {string} Tam API URL
 */
export const getApiUrl = (path, customDomain = null) => {
    const baseUrl = getApiBaseUrl(customDomain);
    
    // Path zaten base URL içeriyorsa direkt döndür
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    
    // Path / ile başlamıyorsa ekle
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    return `${baseUrl}${normalizedPath}`;
};

/**
 * Domain bilgilerini debug için döndürür
 * @returns {object} Domain bilgileri
 */
export const getDomainInfo = () => {
    const domain = getCurrentDomain();
    const environment = getEnvironment();
    const isTr = isComTrDomain(domain);
    const apiUrl = getApiBaseUrl();
    
    return {
        domain,
        environment,
        isComTr: isTr,
        apiBaseUrl: apiUrl
    };
};

// Default export
export default {
    getCurrentDomain,
    isComTrDomain,
    getEnvironment,
    getApiBaseUrl,
    getApiUrl,
    getDomainInfo
};

